<template>
  <div>
    <div v-show="show_terms" class="pb-terms mb-0 term-selector">
      <div class="title">
        {{ $t('common.select_term') }}
        <div class="d-inline text-sm close">
          <a v-track="{ category: 'plan-builder', action: 'click', label: 'hide-terms' }" class="link-info" @click="show_terms = false" role="button">{{ $t('common.done') }}</a>
        </div>
      </div>
      <div class="term-container row">
        <template v-if="item.product_id === 'registration.transfer'">
          <select-box v-for="term in item.termArray.slice(0, 1)" :key="term" v-track="{ category: 'plan-builder', action: 'click', label: 'term-selection' }" :active="selectedTerm === term" :styles="{ col: 'col-md-4 col-xs-6 term-col' }" @click.native="updateTerm(term)">
            <span slot="text" class="fw-semibold">{{ term }} {{ $tc('common.year', term) }}</span>
            <span slot="price" class="term-title-monthly">${{ (item.price * term).toFixed(2) }}</span>
          </select-box>
        </template>
        <template v-else>
          <div v-for="term in item.termArray" :key="term" class="col-md-4 col-12 ps-0 pe-0">
            <detailed-select-box v-if="getFirstYearDiscountedPrice(term)" :key="term" v-track="{ category: 'plan-builder', action: 'click', label: 'term-selection' }" :terms="term" :active="selectedTerm === term" :styles="{ col: 'term-col' }" :term_type="term > 1 ? 'Years' : 'Year'" @click.native="updateTerm(term)" :default_recommended_terms="getDefaultRecommendedTerm()" :class="term === 3 ? 'mb-4' : 'mb-4'">
              <span slot="savings" class="position-absolute top-m-8 start-p-25">
                <span class="d-label-padding text-nowrap text-white-xl text-center ps-2 pe-2 border-1 radius-3 fw-bold text-sm savings-label text-white">{{ $t('common.save') }} ${{ getDiscount(term) }}</span>
              </span>
              <span slot="text" class="term-title-monthly">${{ calculatePrice(term) }}</span>
              <s slot="monthly-price" v-if="!hasFirstYearDiscountAvailableInTermArray()" class="term-strikethrough term-title-monthly pb-4">${{ (item.price * term).toFixed(2) }}</s>
              <span slot="price" class="term-title-monthly pb-4">${{ (item.price * term).toFixed(2) }}</span>
              <span slot="firstyear" v-if="hasFirstYearDiscountAvailableInTermArray()" class="bundle pb-4 fw-semibold">First year ${{ getFirstYearDiscountedPrice(term) }}</span>
              <span slot="reccomended" v-if="term === 3" class="full-width d-block pb-2 pt-2 text-white text-center fw-semibold text-sm recommended">{{ $t('common.recommended') }}</span>
            </detailed-select-box>
            <select-box v-else :key="term" v-track="{ category: 'plan-builder', action: 'click', label: 'term-selection' }" :active="selectedTerm === term" :styles="{ col: 'term-col' }" @click.native="updateTerm(term)" :class="term === 3 ? 'mb-4' : 'mb-4'">
              <span slot="text" class="fw-semibold pt-4 pb-2">{{ term }} {{ $tc('common.year', term) }}</span>
              <span slot="price" class="term-title-monthly pb-4">${{ (item.price * term).toFixed(2) }}</span>
              <span slot="reccomended" v-if="term === getDefaultRecommendedTerm()" class="full-width d-block pb-2 pt-2 text-white text-center fw-semibold text-sm recommended">{{ $t('common.recommended') }}</span>
            </select-box>
          </div>
        </template>
      </div>
    </div>
    <div v-show="!show_terms" class="pb-domain-term pull-left">
      {{ $t('common.term') }}:
      <a v-track="{ category: 'plan-builder', action: 'click', label: 'show-terms' }" class="link-info" @click="show_terms = true" role="button">{{ item.term }} {{ $tc('common.year', item.term) }}</a>
    </div>
  </div>
</template>

<script>
import DetailedSelectBox from './DetailedSelectBox.vue';
import SelectBox from './SelectBox.vue';
import { mapActions } from 'vuex';

export default {
  name: 'TermSelector',

  components: {
    SelectBox,
    DetailedSelectBox
  },

  props: {
    item: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    globalTerm: {
      required: false
    }
  },

  data() {
    return {
      selectedTerm: this.item.term,
      first_term_discounts: this.item.first_term_discounts,
      show_terms: !this.globalTerm
    };
  },

  watch: {
    item: {
      handler(val) {
        this.selectedTerm = val.term;
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('cart', ['updateProduct']),

    updateTerm(new_term) {
      this.$emit('update:loading', true);
      this.updateProduct({
        id: this.item.id,
        term: new_term
      })
        .then(() => {
          this.$emit('update:loading', false);
        })
        .catch(() => {
          this.$emit('update:loading', false);
        });
    },

    isMultiYearDiscount(term) {
      return this.item.first_term_discounts !== undefined && term in this.item.first_term_discounts;
    },

    getDiscount(term) {
      if (!this.isMultiYearDiscount(term)) {
        return;
      }

      return this.item.first_term_discounts[term];
    },
    calculatePrice(term) {
      let price = this.item.price * term;
      if (!this.isMultiYearDiscount(term)) {
        return price;
      }

      const total = price - this.item.first_term_discounts[term];
      return total.toFixed(2);
    },
    getDefaultRecommendedTerm() {
      return this.item.product_id === 'registration.domain' ? 3 : 2;
    },
    hasFirstYearDiscountAvailableInTermArray() {
      if (this.item.first_term_discounts === undefined || this.item.first_term_discounts === null) {
        return false;
      }

      return Object.keys(this.item.first_term_discounts).length > 0;
    },
    getFirstYearDiscountedPrice(term) {
      if (!this.hasFirstYearDiscountAvailableInTermArray() || !(term in this.item.first_term_discounts)) {
        return '';
      }

      const discounted_price = this.item.price - this.item.first_term_discounts[term];
      return discounted_price.toFixed(2);
    }
  }
};
</script>
